import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Plus, Trash2, ToggleLeft, ToggleRight, Tag, Copy, Infinity as InfinityIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trial duration options offered to the admin.
// "LIFETIME" sends grantsLifetime:true (no trialDays). Otherwise we send trialDays.
const DURATION_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'LIFETIME', label: 'Lifetime' },
];

export default function AdminPromoCodesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [usageLimit, setUsageLimit] = useState(1);
  const [duration, setDuration] = useState('60');  // Session 27 — duration dropdown
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadCodes = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API_URL}/api/admin/promo-codes`, { headers });
      setCodes(resp.data);
    } catch (err) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCodes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreating(true);
    try {
      const isLifetime = duration === 'LIFETIME';
      await axios.post(`${API_URL}/api/admin/promo-codes`, {
        code: newCode.trim(),
        usageLimit: parseInt(usageLimit) || 1,
        // Session 27 — duration controls how long the PROVIDER's trial lasts.
        // Lifetime → grantsLifetime:true (no expiration). Otherwise trialDays in days.
        trialDays: isLifetime ? null : parseInt(duration),
        grantsLifetime: isLifetime,
        notes: notes || null,
      }, { headers });
      toast.success(`Promo code "${newCode.trim().toUpperCase()}" created`);
      setNewCode(''); setUsageLimit(1); setDuration('60'); setNotes('');
      setShowCreate(false);
      loadCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (code) => {
    try {
      await axios.patch(`${API_URL}/api/admin/promo-codes/${code.id}`, {
        active: !code.active,
      }, { headers });
      toast.success(`Code ${code.active ? 'deactivated' : 'activated'}`);
      loadCodes();
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete promo code "${code.code}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${code.id}`, { headers });
      toast.success('Promo code deleted');
      loadCodes();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Tag className="h-5 w-5" /> Promo Codes
              </h1>
              <p className="text-sm text-muted-foreground">{codes.length} total codes</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-2" /> New Code
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Create form */}
        {showCreate && (
          <Card data-testid="create-promo-form">
            <CardHeader><CardTitle className="text-base">Create Promo Code</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    placeholder="e.g. WELCOME50"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    className="uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="limit">Usage Limit</Label>
                  <Input
                    id="limit"
                    type="number"
                    min={1}
                    value={usageLimit}
                    onChange={(e) => setUsageLimit(e.target.value)}
                    data-testid="promo-limit-input"
                  />
                </div>
                <div>
                  <Label htmlFor="duration">Trial Duration</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger id="duration" data-testid="promo-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map(opt => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          data-testid={`promo-duration-option-${opt.value}`}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {duration === 'LIFETIME'
                      ? 'Provider gets lifetime access (no payment required, ever).'
                      : `Provider gets a ${duration}-day trial after applying this code.`}
                  </p>
                </div>
                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Internal note"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="promo-notes-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={creating || !newCode.trim()} data-testid="save-promo-btn">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Code
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : codes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No promo codes yet. Click "New Code" to create one.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {codes.map((code) => (
              <Card key={code.id} data-testid={`promo-card-${code.code}`}>
                <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      onClick={() => copyCode(code.code)}
                      className="font-mono text-lg font-bold tracking-wider hover:text-primary transition-colors"
                      title="Copy code"
                    >
                      {code.code}
                    </button>
                    <Copy
                      className="h-3.5 w-3.5 text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => copyCode(code.code)}
                    />
                    <Badge variant={code.active ? 'default' : 'secondary'}>
                      {code.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span>Used: <strong className="text-foreground">{code.usageCount || 0}</strong> / {code.usageLimit}</span>
                    {code.grantsLifetime ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs font-semibold"
                        data-testid={`promo-duration-badge-${code.code}`}
                      >
                        <InfinityIcon className="h-3 w-3" /> Lifetime
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-xs font-semibold"
                        data-testid={`promo-duration-badge-${code.code}`}
                      >
                        {code.trialDays || 60}-day trial
                      </span>
                    )}
                    {code.notes && <span className="hidden md:inline italic">"{code.notes}"</span>}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleActive(code)}
                      title={code.active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-promo-${code.code}`}
                    >
                      {code.active
                        ? <ToggleRight className="h-5 w-5 text-green-600" />
                        : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteCode(code)}
                      data-testid={`delete-promo-${code.code}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
